import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Api from "../Api/Api.json";

const OwnerLanding = () => {
  const [ownerId, setOwnerId] = useState('');
  const [searchUserId, setSearchUserId] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Initialize with owner_id from localStorage
    const storedOwnerId = localStorage.getItem('semionhealth_owner_id');
    if (storedOwnerId) {
      setOwnerId(storedOwnerId);
    }
  }, []);

  const fetchUserByPhone = async (phone) => {
    if (!phone) return null;
    phone = phone.trim();
    if (phone.startsWith("0")) phone = phone.slice(1);

    const headers = { headers: { "Content-Type": "application/json" } };
    const url = Api.TELEDOC_PROVIDER_API;
    const requestData = {
      task: "getPatientDetails",
      phone: phone,
    };
    const response = await axios.post(url, requestData, headers);
    if (!response.data) {
      throw new Error("Network response was not ok");
    }
    return response.data[0] || null; // Return the first patient found or null if none
  };

  const handleAddUser = () => {
    // TODO: Implement add user functionality
    console.log('Add User clicked');
    alert('Add User functionality will be implemented here');
  };

  const handleDeleteUser = () => {
    // TODO: Implement delete user functionality
    console.log('Delete User clicked');
    alert('Delete User functionality will be implemented here');
  };

  const handleSearchUser = async () => {
    if (!searchUserId.trim()) {
      alert('Please enter a user ID to search');
      return;
    }
    
    setIsSearching(true);
    setSearchResult(null);
    
    try {
      console.log('Searching for user with phone:', searchUserId);
      const userData = await fetchUserByPhone(searchUserId);
      
      if (userData) {
        setSearchResult(userData);
        console.log('User found:', userData);
      } else {
        setSearchResult({ error: 'No user found with this phone number' });
      }
    } catch (error) {
      console.error('Error searching for user:', error);
      setSearchResult({ error: 'Error occurred while searching for user' });
    } finally {
      setIsSearching(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('semionhealth_owner_info');
    localStorage.removeItem('semionhealth_owner_id');
    navigate('/owner');
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-bold text-gray-800">Owner Dashboard</h1>
            <div className="flex items-center space-x-4">
              <span className="text-gray-600">Owner ID: {ownerId}</span>
              <button 
                onClick={handleLogout}
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-1 gap-6 mb-8">
            <div className="bg-blue-50 p-6 rounded-lg">
              <div className="text-2xl font-semibold text-blue-800 mb-4">Search User</div>
              <div className="space-y-3">
                <input
                  type="number"
                  placeholder="Enter User ID"
                  value={searchUserId}
                  onChange={(e) => setSearchUserId(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  onClick={handleSearchUser}
                  disabled={isSearching}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed"
                >
                  {isSearching ? 'Searching...' : 'Search User'}
                </button>
              </div>
            </div>
          </div>

          {searchResult && (
            <div className="bg-white border border-gray-200 rounded-lg shadow-md p-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Search Result</h3>
              
              {searchResult.error ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <div className="text-red-600 font-semibold">Error:</div>
                    <div className="text-red-700 ml-2">{searchResult.error}</div>
                  </div>
                </div>
              ) : (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="text-green-600 font-semibold mb-3">User Found:</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    {Object.entries(searchResult)
                      .filter(([key]) => key.toLowerCase() !== 'id') // Remove ID field from display
                      .map(([key, value]) => (
                        <div key={key} className="flex flex-col">
                          <span className="text-sm font-medium text-gray-600 capitalize">
                            {key.replace(/([A-Z])/g, ' $1').trim()}:
                          </span>
                          <span className="text-gray-800 font-semibold">
                            {value !== null && value !== undefined ? value.toString() : 'N/A'}
                          </span>
                        </div>
                      ))}
                  </div>
                  
                  {/* User Action Buttons */}
                  <div className="flex gap-3 mt-4">
                    <button
                      onClick={handleAddUser}
                      className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition-colors flex-1"
                    >
                      Add User
                    </button>
                    <button
                      onClick={handleDeleteUser}
                      className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition-colors flex-1"
                    >
                      Delete User
                    </button>
                    <button
                      onClick={() => {
                        console.log('View User Data clicked for:', searchResult);
                        navigate('/owner/user-data', { 
                          state: { userData: searchResult } 
                        });
                      }}
                      className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors flex-1"
                    >
                      View User Data
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="mt-8 bg-gray-50 p-6 rounded-lg">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Quick Stats</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600">1,234</div>
                <div className="text-gray-600">Total Users</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600">89</div>
                <div className="text-gray-600">Active Users</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600">45</div>
                <div className="text-gray-600">New This Month</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OwnerLanding;
